import type { CatalogItem } from "@/lib/airtable";

/**
 * Single source of truth for matching a catalog taxonomy value (category type,
 * subcategory, or tag descriptor) against a catalog item.
 *
 * The rule: a case-insensitive, word-boundary match that ALSO tolerates a
 * trailing plural "s" on the final word, so one canonical config value matches
 * both singular and plural catalog data. Concretely, a singular config "ring"
 * matches "Ring" and "Rings"; "Stud" matches "STUD" and "STUDS"; "Drop" matches
 * "DROP" and "DROPS". Airtable's Collections field stores these values
 * inconsistently (e.g. "STUDS"/"HOOPS" plural but "HUGGY"/"DROP" singular), and
 * this `s?` tolerance is what bridges that.
 *
 * This helper exists because the category and subcategory filters used to carry
 * two near-identical regex builders that differed by exactly this `s?` — and
 * that one-character divergence was the "Drops/Huggies return nothing" bug. Keep
 * every taxonomy filter on this helper so they can never drift apart again.
 */
const escapeForRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Build the canonical taxonomy-match regex for a config value. Word-boundary,
 * case-insensitive, plural-tolerant on the trailing word.
 */
export const buildTaxonomyRegex = (match: string): RegExp =>
  new RegExp(`\\b${escapeForRegex(match)}s?\\b`, "i");

/**
 * True when `match` hits the item's type, tags, or subcategory (Collections)
 * arrays. Used by both the category and subcategory browse filters, which look
 * across all three fields so a row surfaces whether the descriptor lives in its
 * type ("Solitaire Ring"), a tag, or a Collections value.
 */
export const itemMatchesTaxonomy = (item: CatalogItem, match: string): boolean => {
  const re = buildTaxonomyRegex(match);
  return (
    re.test(item.type || "") ||
    item.tags.some((t) => re.test(t)) ||
    item.subcategory.some((s) => re.test(s))
  );
};
